<script setup>
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { loadScript } from 'dashboard/helper/DOMHelpers';
import { messengerOauthScopeString } from 'custom/dashboard/helper/channelCatalog';
import Button from 'dashboard/components-next/button/Button.vue';
import * as Sentry from '@sentry/vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t } = useI18n();
const store = useStore();
const { accountId } = useAccount();
const busy = ref(false);
const sdkReady = ref(false);
const sdkLoading = ref(false);

const FACEBOOK_SDK_URL = 'https://connect.facebook.net/en_US/sdk.js';
const SDK_WAIT_MS = 15000;
const DIAGNOSTIC_ACCOUNT_PUBLIC_IDS = new Set(['jabjxwug3e', 'eeu7434n2t']);
let facebookSdkPromise = null;
let diagnosticAttemptId = null;
let diagnosticAttemptStartedAt = null;

const createDiagnosticAttemptId = () => {
  return (
    window.crypto?.randomUUID?.() ||
    `${Date.now()}-${Math.random().toString(16).slice(2)}`
  );
};

const currentDashboardAsset = () => {
  const src = Array.from(document.scripts)
    .map(script => script.src)
    .find(scriptSrc => scriptSrc.includes('/vite/assets/dashboard-'));
  return src?.split('/').pop() || null;
};

const sendReauthorizationDiagnostic = (event, details) => {
  if (!window.axios) return;
  if (!DIAGNOSTIC_ACCOUNT_PUBLIC_IDS.has(accountId.value)) return;

  const userActivation = window.navigator.userActivation;
  const payload = {
    event,
    inbox_id: props.inbox.id,
    attempt_id: diagnosticAttemptId,
    asset_version: currentDashboardAsset(),
    sdk_present: typeof window.FB !== 'undefined',
    sdk_ready: sdkReady.value,
    sdk_loading: sdkLoading.value,
    user_activation_active: userActivation?.isActive ?? null,
    user_activation_has_been_active: userActivation?.hasBeenActive ?? null,
    document_has_focus: document.hasFocus(),
    visibility_state: document.visibilityState,
    elapsed_ms: diagnosticAttemptStartedAt
      ? Date.now() - diagnosticAttemptStartedAt
      : null,
    source: details.source,
    status: details.status,
    has_auth_response: details.hasAuthResponse,
    error_name: details.errorName,
    http_status: details.httpStatus,
  };

  window.axios
    .post(
      `/api/v1/accounts/${accountId.value}/callbacks/messenger_reauthorize_event`,
      payload
    )
    .catch(() => {});
};

const loadFacebookSdk = () => {
  if (typeof window.FB !== 'undefined') return Promise.resolve();
  if (facebookSdkPromise) return facebookSdkPromise;

  facebookSdkPromise = new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      const error = new Error('Facebook SDK load timed out');
      error.name = 'FacebookSDKTimeout';
      reject(error);
    }, SDK_WAIT_MS);

    loadScript(FACEBOOK_SDK_URL, { id: 'facebook-jssdk' })
      .then(() => {
        window.clearTimeout(timeout);
        if (typeof window.FB === 'undefined') {
          const error = new Error('Facebook SDK unavailable after load');
          error.name = 'FacebookSDKUnavailable';
          reject(error);
          return;
        }
        resolve();
      })
      .catch(error => {
        window.clearTimeout(timeout);
        reject(error);
      });
  }).catch(error => {
    facebookSdkPromise = null;
    if (typeof window.FB === 'undefined') {
      document.getElementById('facebook-jssdk')?.remove();
    }
    throw error;
  });

  return facebookSdkPromise;
};

const logReauthorizationEvent = (event, details = {}, level = 'info') => {
  // eslint-disable-next-line no-console
  const logger = console[level] || console.info;
  logger('[messenger_reauthorize]', {
    event,
    inboxId: props.inbox.id,
    ...details,
  });
  sendReauthorizationDiagnostic(event, details);
};

const safeErrorDetails = error => ({
  errorName: error?.name || 'Error',
  httpStatus: error?.response?.status || null,
});

const runFBInit = () => {
  window.FB.init({
    appId: window.chatwootConfig.fbAppId,
    xfbml: true,
    version: window.chatwootConfig.fbApiVersion,
    status: true,
  });
  window.fbSDKLoaded = true;
};

const prepareFacebookSdk = async () => {
  if (sdkLoading.value) return;
  sdkLoading.value = true;
  logReauthorizationEvent('sdk_load_started');
  try {
    await loadFacebookSdk();
    runFBInit();
    sdkReady.value = true;
    logReauthorizationEvent('sdk_ready');
  } catch (error) {
    sdkReady.value = false;
    logReauthorizationEvent('sdk_load_error', safeErrorDetails(error), 'error');
    throw error;
  } finally {
    sdkLoading.value = false;
  }
};

const reauthorizeFBPage = async omniauthToken => {
  logReauthorizationEvent('api_request');
  try {
    await store.dispatch('inboxes/reauthorizeFacebookPage', {
      omniauthToken,
      inboxId: props.inbox.id,
    });
    await store.dispatch('inboxes/get');
    logReauthorizationEvent('api_success');
    useAlert(t('INBOX_MGMT.FACEBOOK_REAUTHORIZE.MESSAGE_SUCCESS'));
  } catch (error) {
    logReauthorizationEvent('api_error', safeErrorDetails(error), 'error');
    throw error;
  }
};

const LOGIN_WAIT_MS = 120000;
const POPUP_OPEN_WAIT_MS = 3000;
const FOCUS_STATUS_WAIT_MS = 500;
let waitTimer = null;
let popupGuardTimer = null;
let focusStatusTimer = null;
let windowLostFocus = false;
let loginSettled = false;
let windowBlurHandler = null;
let windowFocusHandler = null;

const clearLoginWatchers = () => {
  if (popupGuardTimer) {
    clearTimeout(popupGuardTimer);
    popupGuardTimer = null;
  }
  if (focusStatusTimer) {
    clearTimeout(focusStatusTimer);
    focusStatusTimer = null;
  }
  if (windowBlurHandler) {
    window.removeEventListener('blur', windowBlurHandler);
    windowBlurHandler = null;
  }
  if (windowFocusHandler) {
    window.removeEventListener('focus', windowFocusHandler);
    windowFocusHandler = null;
  }
};

const clearBusy = () => {
  if (waitTimer) {
    clearTimeout(waitTimer);
    waitTimer = null;
  }
  clearLoginWatchers();
  busy.value = false;
};

const handleLoginResponse = async (response, source) => {
  logReauthorizationEvent('login_callback', {
    source,
    status: response?.status || 'unknown',
    hasAuthResponse: Boolean(response?.authResponse),
  });
  if (loginSettled) return;

  if (response?.status === 'connected' && response.authResponse?.accessToken) {
    loginSettled = true;
    try {
      await reauthorizeFBPage(response.authResponse.accessToken);
    } catch (error) {
      Sentry.captureException(error);
      useAlert(t('INBOX_MGMT.FACEBOOK_REAUTHORIZE.MESSAGE_ERROR'));
    } finally {
      clearBusy();
    }
    return;
  }

  if (source === 'sdk_callback') {
    loginSettled = true;
    clearBusy();
    useAlert(t('INBOX_MGMT.DETAILS.ERROR_FB_AUTH'));
  }
};

const refreshLoginStatus = () => {
  if (loginSettled || typeof window.FB === 'undefined') return;

  const authResponse = window.FB.getAuthResponse?.();
  if (authResponse?.accessToken) {
    handleLoginResponse(
      { status: 'connected', authResponse },
      'focus_auth_response'
    );
    return;
  }

  logReauthorizationEvent('login_status_requested');
  window.FB.getLoginStatus(
    response => handleLoginResponse(response, 'focus_status'),
    true
  );
};

const tryFBlogin = () => {
  loginSettled = false;
  windowLostFocus = false;
  windowBlurHandler = () => {
    windowLostFocus = true;
    logReauthorizationEvent('login_window_blurred');
  };
  windowFocusHandler = () => {
    if (loginSettled) return;
    logReauthorizationEvent('login_window_focused');
    focusStatusTimer = setTimeout(refreshLoginStatus, FOCUS_STATUS_WAIT_MS);
  };
  window.addEventListener('blur', windowBlurHandler);
  window.addEventListener('focus', windowFocusHandler);

  popupGuardTimer = setTimeout(() => {
    popupGuardTimer = null;
    if (windowLostFocus || loginSettled) return;

    logReauthorizationEvent('login_popup_not_detected', {}, 'warn');
    clearBusy();
    useAlert(t('INBOX_MGMT.DETAILS.ERROR_FB_LOADING'));
  }, POPUP_OPEN_WAIT_MS);

  waitTimer = setTimeout(() => {
    waitTimer = null;
    busy.value = false;
    logReauthorizationEvent('login_timeout', {}, 'warn');
    useAlert(t('INBOX_MGMT.DETAILS.ERROR_FB_AUTH'));
  }, LOGIN_WAIT_MS);
  try {
    logReauthorizationEvent('login_requested');
    window.FB.login(response => handleLoginResponse(response, 'sdk_callback'), {
      scope: messengerOauthScopeString(),
      auth_type: 'rerequest',
    });
  } catch (error) {
    clearBusy();
    logReauthorizationEvent('login_error', safeErrorDetails(error), 'error');
    Sentry.captureException(error);
    useAlert(t('INBOX_MGMT.DETAILS.ERROR_FB_AUTH'));
  }
};

const startLogin = () => {
  if (busy.value) return;
  diagnosticAttemptId = createDiagnosticAttemptId();
  diagnosticAttemptStartedAt = Date.now();
  logReauthorizationEvent('clicked', {
    sdkPresent: typeof window.FB !== 'undefined',
    sdkReady: sdkReady.value,
  });

  if (!sdkReady.value || typeof window.FB === 'undefined') {
    logReauthorizationEvent('login_blocked_sdk_not_ready', {}, 'warn');
    useAlert(t('INBOX_MGMT.DETAILS.ERROR_FB_LOADING'));
    prepareFacebookSdk().catch(() => {});
    return;
  }

  busy.value = true;
  tryFBlogin();
};

onMounted(() => {
  logReauthorizationEvent('component_mounted');
  prepareFacebookSdk().catch(() => {});
});

onBeforeUnmount(clearBusy);
</script>

<template>
  <span v-tooltip.top="t('CUSTOM.MESSENGER_AUTH.BAD')" class="inline-flex">
    <Button
      icon="i-lucide-key-round"
      slate
      sm
      :disabled="busy || sdkLoading"
      :is-loading="busy || sdkLoading"
      :label="t('CUSTOM.MESSENGER_AUTH.REAUTHORIZE')"
      @click.stop="startLogin"
    />
  </span>
</template>
